import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import ApiURL from '../Api/Api';
import TransactionProofImage from './TransactionProofImage';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const fmt = (value) =>
  value != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value))
    : 'Rs. 0';

const fmtDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatusBadge = ({ status }) => {
  const normalized = String(status || 'pending').toLowerCase();
  const map = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    inprogress: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${map[normalized] || map.pending}`}>
      {normalized}
    </span>
  );
};

const Stat = ({ label, value, color }) => (
  <div className={`rounded-xl border bg-white px-4 py-3 ${color}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className="mt-1 text-lg font-black text-slate-800">{value}</p>
  </div>
);

const FundRequestHistoryReport = ({ type = 'admin' }) => {
  const { isSidebarOpen } = useContext(SidebarContext);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const userId = localStorage.getItem('id');
  const role = localStorage.getItem('role') || '';
  const isAdminHistory = type === 'admin';

  const title = isAdminHistory ? 'Admin Request History' : 'Worker Request History';
  const subtitle = isAdminHistory
    ? 'All admin fund requests from company owner, including approved and rejected.'
    : 'All worker fund requests from admin, including approved and rejected.';

  const fetchRows = async () => {
    setIsLoading(true);
    setError('');
    try {
      const endpoint = isAdminHistory ? '/getAdminRequests.php' : '/getWorkerRequests.php';
      const params = isAdminHistory
        ? { user_id: userId, role, include_all: 1 }
        : { user_id: userId, role };
      const response = await axios.get(`${ApiURL}${endpoint}`, { params });
      const data = isAdminHistory ? response.data.data : response.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load request history');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!userId || !role) {
      setError('Access denied');
      setIsLoading(false);
      return;
    }
    fetchRows();
  }, [userId, role, type]);

  const filtered = useMemo(() => {
    let list = [...rows];
    if (statusFilter !== 'all') {
      list = list.filter((row) => String(row.status || '').toLowerCase() === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((row) =>
        String(row.id || '').includes(q) ||
        String(row.admin_name || row.admin_user_name || row.user_name || '').toLowerCase().includes(q) ||
        String(row.purpose || '').toLowerCase().includes(q) ||
        String(row.site || '').toLowerCase().includes(q) ||
        String(row.project_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, statusFilter, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    pending: rows.filter((row) => ['pending', 'inprogress'].includes(String(row.status || '').toLowerCase())).length,
    approved: rows.filter((row) => String(row.status || '').toLowerCase() === 'approved').length,
    rejected: rows.filter((row) => String(row.status || '').toLowerCase() === 'rejected').length,
  }), [rows]);

  const exportCSV = () => {
    const headers = ['#', 'Request ID', 'Requester', 'Purpose', 'Amount', 'Site', 'Project', 'Main Head', 'Sub Head', 'Status', 'Created At', 'Updated At', 'Rejection Reason'];
    const csvRows = filtered.map((row, index) => [
      index + 1,
      row.id || '',
      row.admin_name || row.admin_user_name || row.user_name || '',
      row.purpose || '',
      row.amount || 0,
      row.site || '',
      row.project_name || '',
      row.main_head || '',
      row.sub_head || '',
      row.status || '',
      fmtDate(row.created_at),
      fmtDate(row.updated_at),
      row.rejection_reason || '',
    ]);
    const csv = [headers, ...csvRows].map((row) => row.map((cell) => {
      const value = String(cell ?? '');
      return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
    }).join(',')).join('\n');
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    anchor.download = `${isAdminHistory ? 'admin' : 'worker'}_request_history.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className={`min-h-screen flex-1 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="mx-auto max-w-7xl px-3 py-4 sm:px-5 lg:px-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-lg font-black text-slate-800 sm:text-2xl">{title}</h1>
                <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={fetchRows} className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600">
                  <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button onClick={exportCSV} disabled={!filtered.length} className="inline-flex h-9 items-center gap-2 rounded-xl bg-indigo-600 px-3 text-xs font-bold text-white disabled:opacity-50">
                  <DocumentArrowDownIcon className="h-4 w-4" />
                  Export
                </button>
              </div>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="Total" value={stats.total} color="border-slate-100" />
              <Stat label="Pending" value={stats.pending} color="border-amber-100" />
              <Stat label="Approved" value={stats.approved} color="border-emerald-100" />
              <Stat label="Rejected" value={stats.rejected} color="border-rose-100" />
            </div>

            <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name, purpose, site, project, or request ID..."
                  className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-400"
                />
                <div className="flex gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                  {['all', 'pending', 'approved', 'rejected'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`rounded-lg px-3 text-xs font-bold capitalize ${statusFilter === status ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error ? <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] text-left text-xs">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      {['#', 'Requester', 'Purpose', 'Amount', 'Site', 'Project', 'Main Head', 'Sub Head', 'Status', 'Proof', 'Created', 'Updated', 'Rejection Reason'].map((header) => (
                        <th key={header} className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {isLoading ? (
                      <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-400">Loading history...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-400">No request history found</td></tr>
                    ) : filtered.map((row, index) => (
                      <tr key={`${row.id}-${index}`} className="hover:bg-slate-50">
                        <td className="px-3 py-3 font-semibold text-slate-400">{index + 1}</td>
                        <td className="px-3 py-3 font-bold text-slate-700">{row.admin_name || row.admin_user_name || row.user_name || '-'}</td>
                        <td className="max-w-[180px] truncate px-3 py-3 text-slate-600">{row.purpose || '-'}</td>
                        <td className="px-3 py-3 font-bold text-slate-800">{fmt(row.amount)}</td>
                        <td className="px-3 py-3 text-slate-500">{row.site || '-'}</td>
                        <td className="px-3 py-3 text-slate-500">{row.project_name || '-'}</td>
                        <td className="px-3 py-3 text-slate-500">{row.main_head || '-'}</td>
                        <td className="px-3 py-3 text-slate-500">{row.sub_head || '-'}</td>
                        <td className="px-3 py-3"><StatusBadge status={row.status} /></td>
                        <td className="px-3 py-3"><TransactionProofImage path={row.attachment_path} /></td>
                        <td className="px-3 py-3 text-[11px] text-slate-400">{fmtDate(row.created_at)}</td>
                        <td className="px-3 py-3 text-[11px] text-slate-400">{fmtDate(row.updated_at)}</td>
                        <td className="max-w-[180px] truncate px-3 py-3 text-rose-500">{row.rejection_reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default FundRequestHistoryReport;
